import {
  GiftCardRecipient,
  PublicGiftCardSerializer,
} from "@app-contexts/public/features/gift-cards/data/types";
import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import { TicketBasketType } from "@shared/modules/payments/types";
import { UserBasedTicketBasketSerializer } from "@app-contexts/user/features/basket/data/types/basket";

import { AxiosResponse } from "axios";
import _ from "lodash";
import filter from "lodash/filter";
import find from "lodash/find";
import get from "lodash/get";
import reject from "lodash/reject";
import set from "lodash/set";
import sumBy from "lodash/sumBy";
import { UseMutationResult } from "react-query";
import { TicketItemGroupType } from "../types/common/ticket-item-group/ticket-item-group-type";
import { TicketBasketCustomSubTicketType } from "../types";

/**
 * @deprecated. Entire file is deprecated. See useBasket.
 */

/**
 * @param basket basket object
 * @param includeChildren whether or not to include child groups
 * @return {*} lodash list of item groups
 */
const $getItemGroups = (
  { item_groups = [] }: TicketBasketType,
  { includeChildren = false } = {}
): Array<TicketItemGroupType> => {
  return !includeChildren
    ? item_groups.filter(({ parent }) => !parent) // Ignore child groups
    : item_groups;
};

/**
 * Retrieves the specified item group based on the specified criteria, or creates a new one
 */
export const getOrCreateItemGroup = (
  basket: TicketBasketType,
  filters?: TicketItemGroupType,
  opts?: { locationPermissionID: number }
): TicketItemGroupType => {
  const newBasket = basket;
  set(newBasket, "item_groups", get(basket, "item_groups", [])); // Initialize item group array, if not done

  const isDonation =
    filters?.item_type === ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id;

  let group = find(
    $getItemGroups(basket, {
      includeChildren: Boolean(filters?.parent),
    }),
    filters
  );

  // Only 1 location permission ID allowed per item group so
  // create new group if location permission is different
  // or not a custom package location selection
  if (
    group &&
    opts &&
    opts.locationPermissionID &&
    group.tt_location_permissions &&
    !group.tt_location_permissions.includes(opts.locationPermissionID) &&
    !group.requires_seat_or_location_selection
  ) {
    group = null;
  }

  if (group && isDonation) {
    group.donation_amount = filters?.donation_amount;
  }

  if (!group) {
    group = {
      survey_responses: [],
      ...filters,
    };
    newBasket.item_groups?.push(group as TicketItemGroupType);
  }

  // Seat default values, if not already set
  group.tt_seat_permissions = group.tt_seat_permissions || [];
  group.tt_location_permissions = group.tt_location_permissions || [];
  group.scan_codes = group.scan_codes || [];
  group.survey_responses = group.survey_responses || [];

  return group;
};

/**
 * Retrieves the specified item group based on the specified ticket type, or creates a new one
 * @param basket ticket basket
 * @param typeID ticket type ID
 */
export const getOrCreateTicketTypeItemGroup = (
  basket: TicketBasketType,
  typeID: number
): TicketItemGroupType => {
  return getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
    type: typeID,
  });
};

/**
 * Retrieves the specified item group based on the specified product attribute, or creates a new one
 * @param basket ticket basket
 * @param paID product attribute ID
 * @return {*}
 */
const getOrCreateTicketPAGroup = (
  basket: TicketBasketType,
  paID: number
): TicketItemGroupType => {
  return getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
    product_attribute: paID,
  });
};

/**
 * Removes any groups with the specified product attribute
 */
const removeItemGroupsForProductAttribute = (
  basket: TicketBasketType,
  paID: number
): void => {
  const newBasket = basket;
  newBasket.item_groups = reject(
    newBasket.item_groups,
    (group) => !group.parent && group.product_attribute === paID
  );
};

/**
 * Add the specified location permission and membership to the basket
 */
const getOrCreateMembershipLocationPermissionItemGroup = (
  basket: TicketBasketType,
  mlID: number,
  locationPermissionID: number,
  parent = null
): TicketItemGroupType => {
  const filters = {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
    membership_level: mlID,
    parent,
  };

  let group;
  if (locationPermissionID) {
    group = getOrCreateItemGroup(basket, filters, { locationPermissionID });
  } else {
    group = getOrCreateItemGroup(basket, filters);
  }

  if (group.tt_location_permissions && !group.tt_location_permissions.length) {
    group.tt_location_permissions.push(locationPermissionID);
  }

  return group;
};

/**
 * Add the specified location permission and ticket type to the basket
 */
const getOrCreateTicketLocationPermissionItemGroup = (
  basket: TicketBasketType,
  typeID: number,
  locationPermissionID: number,
  parent = null
): TicketItemGroupType => {
  const filters = {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
    type: typeID,
    parent,
  };

  let group;
  if (locationPermissionID) {
    group = getOrCreateItemGroup(basket, filters, { locationPermissionID });
  } else {
    group = getOrCreateItemGroup(basket, filters);
  }

  if (group.tt_location_permissions && !group.tt_location_permissions.length) {
    group.tt_location_permissions.push(locationPermissionID);
  }

  return group;
};

/**
 * Removes any groups with the specified ticket type
 */
export const removeItemGroupsForTicketType = (
  basket: TicketBasketType,
  typeID: number,
  locationPermissionID = null
): void => {
  const newBasket = basket;
  if (!locationPermissionID) {
    newBasket.item_groups = reject(
      newBasket.item_groups,
      (group) => group.type === typeID
    );
  } else {
    newBasket.item_groups = reject(
      newBasket.item_groups,
      (group) =>
        group.type === typeID &&
        group.tt_location_permissions?.[0] === locationPermissionID
    );
  }
};

/**
 * Retrieves the specified item group based on the specified membership level, or creates a new one
 * @param basket ticket basket
 * @param mlID membership level ID
 * @return {*}
 */
export const getOrCreateTicketMLGroup = (
  basket: TicketBasketType,
  mlID: number
): TicketItemGroupType => {
  return getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
    membership_level: mlID,
  });
};

/**
 * Removes any groups with the specified membership level
 */
export const removeItemGroupsForMembershipLevel = (
  basket: TicketBasketType,
  mlID: number
): void => {
  const newBasket = basket;
  newBasket.item_groups = reject(
    newBasket.item_groups,
    (group) => !group.parent && group.membership_level === mlID
  );
};

/**
 * @return {Number} number of tickets in the basket for the specified product attribute
 */
export const getQuantityForProductAttribute = (
  basket: TicketBasketType,
  paID: number
): number => {
  const group = find(basket.item_groups, { product_attribute: paID });

  return (group && group.quantity) || 0;
};

/**
 * @return {Number} number of tickets in the basket for the specified membership level
 */
export const getQuantityForMembershipLevel = (
  basket: TicketBasketType,
  mlID: number
): number => {
  let all_groups = [];
  let quantity = 0;
  if (basket.item_groups) {
    all_groups = basket.item_groups.filter(
      (membership) => membership.membership_level === mlID
    );

    for (let i = 0; i < all_groups.length; i += 1) {
      if (all_groups && all_groups[i]) {
        quantity += all_groups[i]?.quantity ?? 0;
      }
    }
  }

  return (all_groups.length && quantity) || 0;
};

export const setQuantityForMembershipLevel = (
  basket: TicketBasketType,
  mlID: number,
  quantity: number,
  locationPermissionID?: number,
  renewal_member?: number
): TicketBasketType => {
  const newBasket = basket;

  if (quantity === 0) {
    // If quantity is zero then return
    removeItemGroupsForMembershipLevel(basket, mlID);
  } else {
    // Update group quantity
    let group;

    if (!locationPermissionID) {
      group = getOrCreateTicketMLGroup(basket, mlID);
    } else {
      group = getOrCreateMembershipLocationPermissionItemGroup(
        basket,
        mlID,
        locationPermissionID
      );
    }

    // Since all memberships require guest info they are split into 1 item group per memberships.
    // So this extra logic accounts for that. (Exactly how it is done for tickets with info_required = True)
    if (group.id && group.info_required) {
      let membershipGroups = filter(basket.item_groups, {
        membership_level: mlID,
      });
      const allCount = membershipGroups.reduce(
        (total, itemGroup: TicketItemGroupType) => total + itemGroup.quantity,
        0
      );
      if (allCount > quantity) {
        // requesting less than what's in cart
        membershipGroups = membershipGroups.slice(0, quantity);
        newBasket.item_groups = reject(
          basket.item_groups,
          (itemGroup) => itemGroup.membership_level === mlID
        );
        newBasket.item_groups = basket.item_groups?.concat(membershipGroups);
      } else {
        // requesting more than what's in cart
        membershipGroups[0].quantity += quantity - allCount;
      }
    } else {
      group.quantity = quantity;
    }
    // Set purchase as membership renewal if quantity is GTE 1 and member provided.
    if (renewal_member && quantity >= 1) {
      group.renewal_member = renewal_member;
    }
  }

  return newBasket;
};

/**
 * Edits the number of products in the basket for the specified product attribute, removing the
 * item group if/where needed
 */
export const setQuantityForProductAttribute = (
  basket: TicketBasketType,
  paID: number,
  quantity: number
): TicketBasketType => {
  if (quantity === 0) {
    // If quantity is zero, then delete any groups matching type
    removeItemGroupsForProductAttribute(basket, paID);
  } else {
    // Update group quantity
    const group = getOrCreateTicketPAGroup(basket, paID);
    group.quantity = quantity;
  }

  return basket;
};

/**
 * Edits the number of tickets in the basket for the specified ticket type, removing the
 * ticket item group if/where needed
 */
export const setQuantityForTicketType = (
  basket: TicketBasketType,
  typeID: number,
  quantity: number,
  locationPermissionID = null,
  parent = null
): TicketBasketType => {
  const newBasket = basket;

  if (quantity === 0) {
    // If quantity is zero, then delete any groups matching type
    removeItemGroupsForTicketType(basket, typeID, locationPermissionID);
    // TODO: implement custom packages
    // removeSelectedCustomSubTicketOptions(basket, typeID);
  } else {
    // Update group quantity
    let group;

    if (!locationPermissionID) {
      group = getOrCreateTicketTypeItemGroup(basket, typeID);
    } else {
      group = getOrCreateTicketLocationPermissionItemGroup(
        basket,
        typeID,
        locationPermissionID,
        parent
      );
    }

    // TODO: add unit tests for info groups
    if (group.info_required) {
      let collectInfoGroups;
      if (!locationPermissionID) {
        collectInfoGroups = filter(newBasket.item_groups, {
          info_required: true,
          type: typeID,
        });
      } else {
        collectInfoGroups = filter(newBasket.item_groups, {
          info_required: true,
          type: typeID,
          tt_location_permissions: [locationPermissionID],
        });
      }

      const allCount = collectInfoGroups.reduce(
        (total, itemGroup) => total + parseInt(itemGroup.quantity, 10),
        0
      );
      if (allCount > quantity) {
        // requesting less than what's in cart
        collectInfoGroups = collectInfoGroups.slice(0, quantity);
        newBasket.item_groups = reject(
          basket.item_groups,
          (itemGroup) => itemGroup.info_required && itemGroup.type === typeID
        );
        newBasket.item_groups =
          newBasket.item_groups?.concat(collectInfoGroups);
      } else {
        // requesting more than what's in cart
        collectInfoGroups[0].quantity += quantity - allCount;
      }
    } else {
      group.quantity = quantity;
    }
  }
  return newBasket;
};

export const getOrCreateGiftCardItemGroup = (
  basket: TicketBasketType,
  recipient: GiftCardRecipient,
  gcID: PublicGiftCardSerializer["id"]
) => {
  return getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_GIFT_CARD.id,
    gift_card_denomination: recipient.denomination,
    gift_card: gcID,
    quantity: recipient.quantity,
    gift_card_recipient: {
      recipient_name: recipient.recipient_name,
      recipient_email: recipient.recipient_email,
      custom_message: recipient.custom_message,
      notify_on: recipient.notify_on,
      sent_by_username: recipient.sent_by_username,
    },
  });
};

export const setQuantityForGiftCard = (
  basket: TicketBasketType,
  recipients: Array<GiftCardRecipient>,
  gcID: PublicGiftCardSerializer["id"]
) => {
  // Create gift card specific item groups
  recipients.map((recipient) =>
    getOrCreateGiftCardItemGroup(basket, recipient, gcID)
  );

  // Add new gift card item groups to basket, without replacing item groups for other item types

  return basket;
};

export const regularItemGroupsV1 = (
  basket: TicketBasketType
): Array<TicketItemGroupType> => {
  return reject(basket.item_groups, "parent"); // Ignore sub-tickets
};

export const regularItemGroupsV2 = (
  item_groups: TicketBasketType["item_groups"]
): Array<TicketItemGroupType> => {
  return reject(item_groups, "parent"); // Ignore sub-tickets
};

/**
 * returns all non-bundled item groups
 */
export const regularItemGroups = (
  basketOrItemGroup: TicketBasketType | TicketBasketType["item_groups"]
): Array<TicketItemGroupType> => {
  if ("item_groups" in basketOrItemGroup) {
    return regularItemGroupsV1(basketOrItemGroup);
  }
  return regularItemGroupsV2(
    basketOrItemGroup as TicketBasketType["item_groups"]
  ); // cast here as TS cannot infer due to item_groups being incorrectly optional on TicketBasketType
};

export type CartMutationsType = {
  update: UseMutationResult<AxiosResponse<UserBasedTicketBasketSerializer>>;
  expire: UseMutationResult;
};

export type CartQueriesType = {
  get: (id: string) => Promise<UserBasedTicketBasketSerializer>;
};

/**
 * @return {Number} number of tickets in the basket for the specified ticket type (not including
 * sub-tickets)
 */
export const getQuantityForTicketTypeV1 = (
  basket: TicketBasketType,
  typeID: number
): number => {
  const groups = filter(regularItemGroups(basket.item_groups), {
    type: typeID,
  }); // Ignore sub-tickets
  return sumBy(groups, "quantity");
};

/**
 * @return {Number} number of tickets in the basket for the specified ticket type (not including
 * sub-tickets)
 */
export const getQuantityForTicketTypeV2 = (
  item_groups: TicketBasketType["item_groups"],
  typeID: number
): number => {
  const groups = filter(regularItemGroups(item_groups), { type: typeID }); // Ignore sub-tickets
  return sumBy(groups, "quantity");
};

/**
 * @return {Number} number of tickets in the basket for the specified ticket type (not including
 * sub-tickets)
 */
export const getQuantityForTicketType = (
  basketOrItemGroup: TicketBasketType | TicketBasketType["item_groups"],
  typeID: number
): number => {
  if ("item_groups" in basketOrItemGroup) {
    return getQuantityForTicketTypeV1(basketOrItemGroup, typeID);
  }
  return getQuantityForTicketTypeV2(
    basketOrItemGroup as TicketBasketType["item_groups"], // cast here as TS cannot infer due to item_groups being incorrectly optional on TicketBasketType
    typeID
  );
};

/**
 * Add donation amount to the basket
 * @param basket ticket basket
 * @param amount donation amount
 * @return {*}
 */
function getOrCreateTicketDonationGroup(basket, amount, charityID) {
  return getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
    donation_amount: amount,
    charity_id: charityID,
  });
}

/**
 * Edits the donation amount in the basket
 */
export const setAmountForDonation = (
  basket: TicketBasketType,
  amount: number,
  charityID: string
): TicketBasketType => {
  if (amount === 0) {
    const newItemGroups = reject(
      basket.item_groups,
      (group) => group.item_type === ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id
    );
    return { ...basket, item_groups: newItemGroups };
  }

  const group = getOrCreateTicketDonationGroup(basket, amount, charityID);
  group.quantity = 1;
  return basket;
};

/**
 * Adds the specified seat permission and ticket type to the basket
 */
export const addTicketSeatPermission = (
  basket: TicketBasketType,
  typeID: number,
  permissionID: number,
  itemGroupParent = null
): void => {
  // Get/create item group
  const group = getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
    type: typeID,
    ...(itemGroupParent && { parent: itemGroupParent }),
  });

  // Add seat permission
  group.tt_seat_permissions?.push(permissionID);
  group.quantity = group.tt_seat_permissions?.length;
};

/**
 * Adds the specified seat permission and ticket type to the basket
 */
export const addMembershipSeatPermission = (
  basket: TicketBasketType,
  mlID: number,
  permissionID: number,
  itemGroupParent = null
): void => {
  // Get/create item group
  const group = getOrCreateItemGroup(basket, {
    item_type: ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
    membership_level: mlID,
    ...(itemGroupParent && { parent: itemGroupParent }),
  });

  // Add seat permission
  group.tt_seat_permissions?.push(permissionID);
  group.quantity = group.tt_seat_permissions?.length;
};

/**
 * Removes the item group with the specified item group ID
 */
const removeItemGroup = (basket: TicketBasketType, groupID: number) => {
  const newItemGroups = reject(
    basket.item_groups,
    (group) => group.id === groupID
  );
  set(basket, "item_groups", newItemGroups);
};

/**
 * Removes the specified seat permission from the basket
 */
export const removeSeatPermission = (
  basket: TicketBasketType,
  permissionID: number,
  itemGroupParent = null
): void => {
  $getItemGroups(basket, { includeChildren: Boolean(itemGroupParent) })
    // get item groups with tt_seat_permissions
    .filter(({ tt_seat_permissions }) => (tt_seat_permissions?.length ?? 0) > 0)
    .forEach((group) => {
      // remove the given permission from the seat permissions
      if (group.id) {
        const newSeatPerms = filter(
          group.tt_seat_permissions,
          (id) => id !== permissionID
        );
        const locationPermissionQty =
          group.tt_location_permissions?.length ?? 0;

        // by removing some seat permissions from this item group,
        // the remaining quantity will be the seat permissions + location permissions of the same ticket type
        const newQuantity = newSeatPerms.length + locationPermissionQty;

        set(group, "tt_seat_permissions", newSeatPerms);
        set(group, "quantity", newQuantity);

        if (newQuantity === 0) {
          removeItemGroup(basket, group.id);
        }
      }
    });
};

/**
 * Removes items from the basket's selected_custom_sub_ticket_options
 * with a parent ticket type value that match the given ID.
 * @param basket Shopping cart basket
 * @param typeID Ticket type ID
 */
function removeSelectedCustomSubTicketOptions(
  basket: TicketBasketType,
  typeID: number
) {
  if (basket?.selected_custom_sub_ticket_options) {
    // eslint-disable-next-line
    _.remove(
      basket.selected_custom_sub_ticket_options as TicketBasketCustomSubTicketType[],
      (option: TicketBasketCustomSubTicketType) => {
        return option.parent_ticket_type === typeID;
      }
    );
  }
}

/**
 * Add selected custom package options to basket object
 */
export function setSelectedPackageOptions(
  basket: TicketBasketType,
  selectedPackageOptions: Array<{
    custom_sub_ticket_option: number;
    parent_ticket_type: number;
  }>
) {
  const newBasket = basket;
  if (!newBasket.selected_custom_sub_ticket_options) {
    newBasket.selected_custom_sub_ticket_options = [];
  }
  // Remove options that have the same parent ticket type before pushing
  removeSelectedCustomSubTicketOptions(
    newBasket,
    selectedPackageOptions[0].parent_ticket_type
  );
  newBasket.selected_custom_sub_ticket_options =
    newBasket.selected_custom_sub_ticket_options.concat(selectedPackageOptions);

  return newBasket;
}
